// Shared types and schemas
// Used by both client and server

import { z } from 'zod'

// Common field schemas
export const emailField = z.string().email('Invalid email')
export const phoneField = z.string().regex(/^[+]?[\d\s-()]+$/, 'Invalid phone')
export const urlField = z.string().url('Invalid URL')
export const uuidField = z.string().uuid('Invalid ID')

// Nullable UUID that converts empty strings to null (for optional UUID inputs like dropdowns)
export const optionalUuidField = z.preprocess(
  (val) => (val === '' || val === undefined ? null : val),
  z.string().uuid('Invalid ID').nullable()
)

// datetime-local input format: "2024-12-15T10:30" or full ISO "2024-12-15T10:30:00.000Z"
export const datetimeField = z.string().refine(
  (val) => !isNaN(Date.parse(val)),
  'Invalid date/time'
)

// Nullable datetime that converts empty strings to null (for optional date inputs)
export const optionalDatetimeField = z.preprocess(
  (val) => (val === '' || val === undefined ? null : val),
  z.string().refine((val) => !isNaN(Date.parse(val)), 'Invalid date/time').nullable()
)

// Pagination
export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
  sort: z.enum(['asc', 'desc']).default('desc'),
})

export type PaginationQuery = z.infer<typeof paginationSchema>

// API Response wrapper
export interface ApiResponse<T> {
  data: T
  meta?: {
    total: number
    page: number
    pageSize: number
  }
}

// ===========================================
// AUTH SCHEMAS
// ===========================================

export const registerSchema = z.object({
  email: emailField,
  password: z.string().min(8, 'Password must be at least 8 characters'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
})

export type RegisterInput = z.infer<typeof registerSchema>

export const loginSchema = z.object({
  email: emailField,
  password: z.string().min(1, 'Password is required'),
})

export type LoginInput = z.infer<typeof loginSchema>

export const verifyTwoFactorSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits'),
  sessionToken: z.string().optional(), // For pending 2FA verification
})

export type VerifyTwoFactorInput = z.infer<typeof verifyTwoFactorSchema>

export const enableTwoFactorSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits'),
})

export type EnableTwoFactorInput = z.infer<typeof enableTwoFactorSchema>

export const disableTwoFactorSchema = z.object({
  password: z.string().min(1, 'Password is required'),
})

export type DisableTwoFactorInput = z.infer<typeof disableTwoFactorSchema>

// Role type
export type Role = 'admin' | 'sales_manager' | 'sales_rep' | 'marketing' | 'service_rep'

// User response type (excludes sensitive fields)
export interface UserResponse {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  role: Role
  managerId?: string | null
  twoFactorEnabled: boolean
  createdAt: string
}

// Assignable user (for owner dropdowns)
export interface AssignableUser {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  role: Role
}

// Login response
export interface LoginResponse {
  user?: UserResponse
  requiresTwoFactor?: boolean
  pendingSessionToken?: string
}

// ===========================================
// ENTITY SCHEMAS
// ===========================================

// Add entity schemas here following pattern:
// notes/patterns/forms/validation-schema.txt

// Lead validation schema
export const leadSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  company: z.string().min(1, 'Company is required'),
  email: emailField.optional(),
  phone: phoneField.optional(),
  status: z.enum(['new', 'contacted', 'qualified', 'unqualified']).default('new'),
  rating: z.enum(['hot', 'warm', 'cold']).optional(),
  source: z.string().optional(),
  ownerId: uuidField.optional(),
  // B2B fields
  city: z.string().optional(),
  street: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  website: z.string().url().optional().or(z.literal('')),
  description: z.string().optional(),
  b2bScore: z.number().int().min(0).max(100).optional(),
  tier: z.string().optional(),
  category: z.string().optional(),
  salesPlay: z.string().optional(),
  digitalSignals: z.string().optional(),
  techStack: z.string().optional(),
  contactQuality: z.enum(['High', 'Medium', 'Low']).optional(),
  hrbNumber: z.string().optional(),
  impressumUrl: z.string().url().optional().or(z.literal('')),
  managingDirector: z.string().optional(),
  emailsScraped: z.string().optional(),
  teamMembers: z.array(z.object({
    name: z.string(),
    title: z.string().optional(),
    source: z.string().optional(),
    category: z.string().optional(),
  })).optional(),
})

export type LeadInput = z.infer<typeof leadSchema>

// Account validation schema
export const accountSchema = z.object({
  name: z.string().min(1, 'Account name is required'),
  type: z.enum(['prospect', 'customer', 'partner']).optional(),
  industry: z.string().optional(),
  phone: phoneField.optional(),
  website: urlField.optional(),
  ownerId: uuidField.optional(), // For admins/managers to assign ownership
})

export type AccountInput = z.infer<typeof accountSchema>

// Contact validation schema
export const contactSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().min(1, 'Last name is required'),
  email: emailField.optional(),
  phone: phoneField.optional(),
  title: z.string().optional(),
  accountId: uuidField,
  useAccountAddress: z.boolean().optional().default(true),
  mailingAddressId: uuidField.optional().nullable(),
  ownerId: uuidField.optional(), // For admins/managers to assign ownership
})

export type ContactInput = z.infer<typeof contactSchema>

// Opportunity validation schema
export const opportunitySchema = z.object({
  name: z.string().min(1, 'Opportunity name is required'),
  amount: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Invalid amount').optional(),
  stage: z.enum(['qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost']).default('qualification'),
  closeDate: datetimeField,
  probability: z.enum(['10%', '25%', '50%', '75%', '90%']).optional(),
  accountId: uuidField,
  contactId: uuidField.optional(),
  shippingAddressId: uuidField.optional().nullable(),
  ownerId: uuidField.optional(), // For admins/managers to assign ownership
})

export type OpportunityInput = z.infer<typeof opportunitySchema>

// Event validation schema
export const eventSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  startTime: datetimeField,
  endTime: datetimeField,
  type: z.enum(['call', 'meeting', 'email']),
  status: z.enum(['scheduled', 'completed', 'cancelled']).default('scheduled'),
  description: z.string().optional(),
  relatedToType: z.enum(['lead', 'account', 'contact', 'opportunity']).optional(),
  relatedToId: uuidField.optional(),
  ownerId: uuidField.optional(), // For admins/managers to assign ownership
})

export type EventInput = z.infer<typeof eventSchema>

// Note validation schema
export const noteSchema = z.object({
  title: z.string().optional(),
  body: z.string().min(1, 'Note body is required'),
  relatedToType: z.enum(['lead', 'account', 'contact', 'opportunity']),
  relatedToId: uuidField,
  ownerId: uuidField.optional(), // For admins/managers to assign ownership
})

export type NoteInput = z.infer<typeof noteSchema>

// Address validation schema
export const addressSchema = z.object({
  type: z.enum(['primary', 'billing', 'shipping']),
  street: z.string()
    .min(1, 'Street is required')
    .max(200, 'Street address is too long'),
  city: z.string()
    .min(1, 'City is required')
    .max(100, 'City name is too long'),
  state: z.string()
    .max(100, 'State/Province is too long')
    .optional()
    .or(z.literal('')),
  postalCode: z.string()
    .max(20, 'Postal code is too long')
    .regex(/^[A-Za-z0-9\s-]*$/, 'Invalid postal code format')
    .optional()
    .or(z.literal('')),
  country: z.string()
    .min(1, 'Country is required')
    .max(100, 'Country name is too long'),
  accountId: uuidField,
})

export type AddressInput = z.infer<typeof addressSchema>

// ===========================================
// FULL ENTITY TYPES (with database fields)
// ===========================================

interface BaseEntity {
  id: string
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
  ownerId?: string | null
}

export interface Lead extends BaseEntity {
  firstName: string | null
  lastName: string | null
  company: string
  email: string | null
  phone: string | null
  status: string
  rating: string | null
  source: string | null
  converted: boolean
  convertedAt: Date | null
  convertedAccountId: string | null
  convertedContactId: string | null
  convertedOpportunityId: string | null
  // B2B fields
  city: string | null
  street: string | null
  postalCode: string | null
  country: string | null
  website: string | null
  description: string | null
  b2bScore: number | null
  tier: string | null
  category: string | null
  salesPlay: string | null
  digitalSignals: string | null
  techStack: string | null
  contactQuality: string | null
  hrbNumber: string | null
  impressumUrl: string | null
  managingDirector: string | null
  emailsScraped: string | null
  teamMembers: Array<{ name: string; title?: string; source?: string; category?: string }> | null
}

export interface Account extends BaseEntity {
  name: string
  type: string | null
  industry: string | null
  phone: string | null
  website: string | null
}

export interface Contact extends BaseEntity {
  firstName: string | null
  lastName: string
  email: string | null
  phone: string | null
  title: string | null
  accountId: string
  useAccountAddress: boolean
  mailingAddressId: string | null
}

export interface Opportunity extends BaseEntity {
  name: string
  amount?: string
  stage: 'qualification' | 'proposal' | 'negotiation' | 'closed_won' | 'closed_lost'
  closeDate: string
  probability?: '10%' | '25%' | '50%' | '75%' | '90%'
  accountId: string
  contactId?: string
  shippingAddressId?: string | null
}

export interface Event extends BaseEntity {
  subject: string
  startTime: string
  endTime: string
  type: string
  status: string
  description: string | null
  relatedToType: string | null
  relatedToId: string | null
}

export interface Note extends BaseEntity {
  title: string | null
  body: string
  relatedToType: string
  relatedToId: string
}

export interface Address extends BaseEntity {
  type: 'primary' | 'billing' | 'shipping'
  street: string
  city: string
  state: string | null
  postalCode: string | null
  country: string
  accountId: string
}

// ===========================================
// CONCIERGE SCHEMAS (Physical Events)
// ===========================================

// Concierge Partner validation schema
export const conciergePartnerSchema = z.object({
  name: z.string().min(1, 'Partner name is required'),
  category: z.enum(['hospitality', 'music', 'wine', 'sport', 'maritime', 'auto']),
  logoUrl: z.string().optional(),
})

export type ConciergePartnerInput = z.infer<typeof conciergePartnerSchema>

// Concierge Event validation schema
export const conciergeEventSchema = z.object({
  titleEn: z.string().min(1, 'English title is required'),
  titleDe: z.string().optional(),
  descriptionEn: z.string().optional(),
  descriptionDe: z.string().optional(),
  partnerId: uuidField.optional().nullable(),
  category: z.enum(['hospitality', 'music', 'wine', 'sport', 'maritime', 'auto']),
  visibility: z.enum(['public', 'patron']).default('public'),
  featured: z.boolean().optional().default(false),
  eventDate: datetimeField,
  location: z.string().min(1, 'Location is required'),
  address: z.string().optional(),
  capacity: z.string().optional(),
  patronCapacity: z.string().optional(),
  imageUrl: z.string().optional(),
  status: z.enum(['upcoming', 'past', 'cancelled']).default('upcoming'),
})

export type ConciergeEventInput = z.infer<typeof conciergeEventSchema>

// ===========================================
// CONCIERGE FULL ENTITY TYPES
// ===========================================

interface BaseConciergeEntity {
  id: string
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | null
}

export interface ConciergePartner extends BaseConciergeEntity {
  name: string
  category: 'hospitality' | 'music' | 'wine' | 'sport' | 'maritime' | 'auto'
  logoUrl: string | null
}

export interface ConciergeEvent extends BaseConciergeEntity {
  titleEn: string
  titleDe: string | null
  descriptionEn: string | null
  descriptionDe: string | null
  partnerId: string | null
  category: 'hospitality' | 'music' | 'wine' | 'sport' | 'maritime' | 'auto'
  visibility: 'public' | 'patron'
  featured: boolean
  eventDate: string
  location: string
  address: string | null
  capacity: string | null
  patronCapacity: string | null
  imageUrl: string | null
  status: 'upcoming' | 'past' | 'cancelled'
}

// ===========================================
// WEB LEADS SCHEMAS
// ===========================================

// Interest types for web lead form
export const webLeadInterestTypes = ['discovery_day', 'pilot_project', 'partnership_request', 'investment_inquiry', 'general_question'] as const
export type WebLeadInterest = typeof webLeadInterestTypes[number]

// Status types for web leads
export const webLeadStatusTypes = ['unviewed', 'viewed', 'converted', 'archived'] as const
export type WebLeadStatus = typeof webLeadStatusTypes[number]

// Public web lead submission schema (from external website)
export const webLeadSubmissionSchema = z.object({
  // Required contact fields
  name: z.string().min(1, 'Name is required'),
  company: z.string().min(1, 'Company is required'),
  email: emailField,
  interest: z.enum(webLeadInterestTypes),
  message: z.string().min(1, 'Message is required'),
  privacyConsent: z.literal(true, { errorMap: () => ({ message: 'Privacy consent is required' }) }),

  // Optional contact fields
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: phoneField.optional().or(z.literal('')),
  jobTitle: z.string().optional(),

  // Form metadata
  formName: z.string().optional(),
  sourceUrl: z.string().optional(),

  // Attribution (UTM)
  referrerUrl: z.string().optional(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmContent: z.string().optional(),
  utmTerm: z.string().optional(),

  // Visitor context (client-provided)
  timezone: z.string().optional(),
  browserLanguage: z.string().optional(),

  // Device & Browser
  deviceType: z.string().optional(),
  os: z.string().optional(),
  browserName: z.string().optional(),
  browserVersion: z.string().optional(),
  screenResolution: z.string().optional(),

  // Session data
  sessionId: z.string().optional(),
  entryPage: z.string().optional(),
  pagesViewed: z.array(z.string()).optional(),
  pageCount: z.number().int().optional(),
  timeOnSite: z.number().int().optional(),
  returningVisitor: z.boolean().optional(),

  // Consent
  analyticsConsent: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),

  // Extended/Optional
  scrollDepth: z.number().int().min(0).max(100).optional(),
  timeOnFormPage: z.number().int().optional(),
  downloadRequested: z.string().optional(),
  productInterest: z.string().optional(),
  preferredContactMethod: z.string().optional(),
  customFields: z.record(z.unknown()).optional(),
})

export type WebLeadSubmissionInput = z.infer<typeof webLeadSubmissionSchema>

// Website config schema (admin)
export const websiteConfigSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  allowedDomains: z.array(z.string().url('Invalid domain URL')).min(1, 'At least one domain is required'),
  isActive: z.boolean().default(true),
})

export type WebsiteConfigInput = z.infer<typeof websiteConfigSchema>

// ===========================================
// WEB LEADS FULL ENTITY TYPES
// ===========================================

export interface WebLead {
  id: string

  // Contact information
  name: string
  firstName: string | null
  lastName: string | null
  company: string
  email: string
  phone: string | null
  jobTitle: string | null

  // Inquiry details
  interest: WebLeadInterest
  message: string
  privacyConsent: boolean
  formName: string | null

  // Status tracking
  status: WebLeadStatus
  viewedAt: Date | null
  viewedById: string | null

  // Source tracking
  websiteConfigId: string
  sourceUrl: string | null
  referrerUrl: string | null
  ipAddress: string | null
  userAgent: string | null

  // UTM Attribution
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
  utmTerm: string | null

  // Visitor Context
  country: string | null
  region: string | null
  city: string | null
  timezone: string | null
  browserLanguage: string | null

  // Device & Browser
  deviceType: string | null
  os: string | null
  browserName: string | null
  browserVersion: string | null
  screenResolution: string | null

  // Session Data
  sessionId: string | null
  entryPage: string | null
  pagesViewed: string[] | null
  pageCount: number | null
  timeOnSite: number | null
  returningVisitor: boolean | null

  // Consent
  analyticsConsent: boolean | null
  marketingConsent: boolean | null

  // Extended/Optional
  scrollDepth: number | null
  timeOnFormPage: number | null
  downloadRequested: string | null
  productInterest: string | null
  preferredContactMethod: string | null
  customFields: Record<string, unknown> | null

  // Conversion tracking
  convertedToLeadId: string | null
  convertedAt: Date | null

  // Audit
  createdAt: Date
}

export interface WebsiteConfig {
  id: string
  name: string
  apiKeyPrefix: string
  allowedDomains: string[] // Parsed from JSON
  isActive: boolean
  createdAt: Date
  updatedAt: Date
}

// ===========================================
// TICKETS SCHEMAS
// ===========================================

// Ticket enums
export const ticketStatuses = ['open', 'in_progress', 'pending', 'resolved', 'closed'] as const
export const ticketPriorities = ['low', 'medium', 'high', 'urgent'] as const
export const ticketCategories = ['support', 'internal', 'development'] as const
export const ticketTypes = ['question', 'problem', 'incident', 'feature_request', 'bug', 'task'] as const
export const ticketChannels = ['email', 'phone', 'web', 'portal', 'admin'] as const

export type TicketStatus = typeof ticketStatuses[number]
export type TicketPriority = typeof ticketPriorities[number]
export type TicketCategory = typeof ticketCategories[number]
export type TicketType = typeof ticketTypes[number]
export type TicketChannel = typeof ticketChannels[number]

// Helper to transform empty strings to null for optional fields
const emptyStringToNull = z.union([
  z.literal('').transform(() => null),
  z.null(),
  z.undefined(),
]).optional()

const optionalUuid = z.union([
  z.literal('').transform(() => null),
  z.null(),
  uuidField,
]).optional().nullable()

const optionalDatetime = z.union([
  z.literal('').transform(() => null),
  z.null(),
  datetimeField,
]).optional().nullable()

const optionalEnum = <T extends readonly [string, ...string[]]>(enumValues: T) =>
  z.union([
    z.literal('').transform(() => null),
    z.null(),
    z.enum(enumValues),
  ]).optional().nullable()

// Ticket validation schema
export const ticketSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  description: z.string().min(1, 'Description is required'),
  status: z.enum(ticketStatuses).default('open'),
  priority: z.enum(ticketPriorities).default('medium'),
  category: z.enum(ticketCategories).default('support'),
  type: optionalEnum(ticketTypes),
  channel: optionalEnum(ticketChannels),
  contactId: optionalUuid,
  accountId: optionalUuid,
  assignedToId: optionalUuid,
  dueDate: optionalDatetime,
})

export type TicketInput = z.infer<typeof ticketSchema>

// Ticket comment validation schema
export const ticketCommentSchema = z.object({
  body: z.string().min(1, 'Comment is required'),
  isInternal: z.boolean().default(false),
})

export type TicketCommentInput = z.infer<typeof ticketCommentSchema>

// ===========================================
// TICKETS FULL ENTITY TYPES
// ===========================================

export interface Ticket extends BaseEntity {
  ticketNumber: string
  subject: string
  description: string
  status: TicketStatus
  priority: TicketPriority
  category: TicketCategory
  type: TicketType | null
  channel: TicketChannel | null
  contactId: string | null
  accountId: string | null
  assignedToId: string | null
  createdById: string
  dueDate: Date | null
  resolvedAt: Date | null
  closedAt: Date | null
}

export interface TicketComment {
  id: string
  ticketId: string
  authorId: string
  body: string
  isInternal: boolean
  createdAt: Date
  updatedAt: Date
}

// Extended ticket types for API responses
export interface TicketWithRelations extends Ticket {
  contact?: { id: string; firstName: string | null; lastName: string } | null
  account?: { id: string; name: string } | null
  assignedTo?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
  createdBy?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
}

export interface TicketCommentWithAuthor extends TicketComment {
  author: { id: string; firstName: string | null; lastName: string | null; email: string }
}

export interface TicketWithComments extends TicketWithRelations {
  comments: TicketCommentWithAuthor[]
}

// ===========================================
// SCANNING EVENTS & CARD SCANS SCHEMAS
// ===========================================

// Scanning Event validation schema (admin CRUD)
export const scanningEventSchema = z.object({
  name: z.string().min(1, 'Event name is required'),
  description: z.string().optional(),
  startDate: datetimeField,
  endDate: datetimeField.optional(),
  location: z.string().optional(),
  isActive: z.boolean().default(true),
})

export type ScanningEventInput = z.infer<typeof scanningEventSchema>

// Card Scan submission schema (mobile API)
export const cardScanSchema = z.object({
  scanningEventId: uuidField,
  rawText: z.string().min(1, 'OCR text is required'),
  scannedAt: datetimeField,
  ocrConfidence: z.number().min(0).max(1).optional(),
  imageBase64: z.string().optional(), // Only sent if confidence < 80%
})

export type CardScanInput = z.infer<typeof cardScanSchema>

// ===========================================
// SCANNING EVENTS FULL ENTITY TYPES
// ===========================================

export interface ScanningEvent extends BaseEntity {
  name: string
  description: string | null
  startDate: Date
  endDate: Date | null
  location: string | null
  isActive: boolean
  createdById: string
}

export interface CardScan {
  id: string
  scanningEventId: string
  scannedById: string
  rawText: string
  ocrConfidence: number | null
  scannedAt: Date
  imageUrl: string | null
  imageStoragePath: string | null
  parsedFirstName: string | null
  parsedLastName: string | null
  parsedCompany: string | null
  parsedEmail: string | null
  parsedPhone: string | null
  parsedTitle: string | null
  parseStatus: 'pending' | 'completed' | 'failed'
  parsedAt: Date | null
  convertedToLeadId: string | null
  convertedAt: Date | null
  isDuplicate: boolean
  duplicateOfId: string | null
  createdAt: Date
}

export interface CardScanWithEvent extends CardScan {
  scanningEvent: { id: string; name: string }
}

// ===========================================
// EMAIL SCHEMAS (Drag & Drop / Outlook Add-In)
// ===========================================

// Email source types
export const emailSources = ['drop_upload', 'outlook_addin', 'api'] as const
export type EmailSource = typeof emailSources[number]

// Email upload schema (for drag & drop)
export const emailUploadSchema = z.object({
  targetType: z.enum(['account', 'lead', 'opportunity', 'auto']).optional(),
  targetId: uuidField.optional(),
})

export type EmailUploadInput = z.infer<typeof emailUploadSchema>

// Email ingest schema (for Outlook Add-In)
export const emailIngestSchema = z.object({
  messageId: z.string().optional(),
  subject: z.string().min(1, 'Subject is required'),
  from: z.object({
    email: emailField,
    name: z.string().optional(),
  }),
  to: z.array(z.object({
    email: emailField,
    name: z.string().optional(),
  })),
  cc: z.array(z.object({
    email: emailField,
    name: z.string().optional(),
  })).optional(),
  sentAt: datetimeField.optional(),
  bodyText: z.string().optional(),
  bodyHtml: z.string().optional(),
  attachments: z.array(z.object({
    name: z.string(),
    contentType: z.string(),
    contentBytes: z.string(), // base64
  })).optional(),
  note: z.string().optional(), // User-added note when importing
})

export type EmailIngestInput = z.infer<typeof emailIngestSchema>

// Email link schema (manual linking)
export const emailLinkSchema = z.object({
  accountId: uuidField.optional().nullable(),
  contactId: uuidField.optional().nullable(),
  leadId: uuidField.optional().nullable(),
  opportunityId: uuidField.optional().nullable(),
})

export type EmailLinkInput = z.infer<typeof emailLinkSchema>

// ===========================================
// EMAIL FULL ENTITY TYPES
// ===========================================

export interface EmailExtractedData {
  dates?: Array<{ text: string; date: string }>;
  amounts?: Array<{ text: string; value: number; currency: string }>;
  competitors?: string[];
  actionItems?: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
}

export interface EmailRecord extends BaseEntity {
  source: EmailSource
  originalFilename: string | null

  // Email metadata
  messageId: string | null
  subject: string
  fromEmail: string
  fromName: string | null
  toEmails: string[]
  ccEmails: string[]
  sentAt: Date | null

  // Content
  bodyText: string | null
  bodyHtml: string | null
  hasAttachments: boolean

  // Linking
  accountId: string | null
  contactId: string | null
  leadId: string | null
  opportunityId: string | null

  // AI Extraction
  extractedData: EmailExtractedData | null

  // Threading
  threadId: string | null

  // Ownership and matching
  uploadedById: string | null
  matchConfidence: number | null
  matchedManually: boolean
}

export interface EmailAttachmentRecord {
  id: string
  emailId: string
  filename: string
  mimeType: string | null
  size: number | null
  storageUrl: string
  storagePath: string | null
  createdAt: Date
}

export interface EmailWithRelations extends EmailRecord {
  account?: { id: string; name: string } | null
  contact?: { id: string; firstName: string | null; lastName: string } | null
  lead?: { id: string; firstName: string | null; lastName: string; company: string } | null
  opportunity?: { id: string; name: string } | null
  uploadedBy?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
  attachments?: EmailAttachmentRecord[]
}

// Email match result from auto-matching
export interface EmailMatchResult {
  accountId?: string
  accountName?: string
  contactId?: string
  contactName?: string
  confidence: number
  matchedBy: 'email' | 'domain' | 'thread' | 'manual'
}

// ===========================================
// SERVICE HUB: ASSETS SCHEMAS
// ===========================================

// Asset status enum
export const assetStatuses = ['active', 'maintenance', 'decommissioned'] as const
export type AssetStatus = typeof assetStatuses[number]

// Asset validation schema
export const assetSchema = z.object({
  serialNumber: z.string().min(1, 'Serial number is required').max(100),
  accountId: uuidField,
  contactId: uuidField.optional().nullable(),
  productId: uuidField.optional().nullable(),
  productLine: z.string().max(100).optional().nullable(),
  model: z.string().max(100).optional().nullable(),
  commissioningDate: datetimeField.optional().nullable(),
  warrantyExpiry: datetimeField.optional().nullable(),
  status: z.enum(assetStatuses).default('active'),
  configuration: z.record(z.unknown()).optional().nullable(),
  twinProjectId: uuidField.optional().nullable(),
  twinSampleId: z.string().max(50).optional().nullable(),
  qrCodeUrl: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
})

export type AssetInput = z.infer<typeof assetSchema>

// Asset full entity type
export interface AssetRecord extends BaseEntity {
  serialNumber: string
  accountId: string
  contactId: string | null
  productId: string | null
  productLine: string | null
  model: string | null
  commissioningDate: Date | null
  warrantyExpiry: Date | null
  status: AssetStatus
  configuration: Record<string, unknown> | null
  twinProjectId: string | null
  twinSampleId: string | null
  qrCodeUrl: string | null
  notes: string | null
}

export interface AssetWithRelations extends AssetRecord {
  account?: { id: string; name: string }
  contact?: { id: string; firstName: string | null; lastName: string } | null
  _count?: { serviceCases: number }
}

// ===========================================
// SERVICE HUB: SERVICE CASES SCHEMAS
// ===========================================

// Service Case enums
export const serviceCaseStatuses = ['open', 'in_progress', 'waiting_customer', 'waiting_parts', 'resolved', 'closed'] as const
export const serviceCasePriorities = ['low', 'medium', 'high', 'critical'] as const
export const serviceCaseCategories = ['part_request', 'error_code', 'maintenance', 'documentation', 'other'] as const
export const serviceCaseChannels = ['portal', 'qr_scan', 'phone', 'email'] as const

export type ServiceCaseStatus = typeof serviceCaseStatuses[number]
export type ServiceCasePriority = typeof serviceCasePriorities[number]
export type ServiceCaseCategory = typeof serviceCaseCategories[number]
export type ServiceCaseChannel = typeof serviceCaseChannels[number]

// Service Case validation schema
// Note: accountId is optional - backend auto-fills from asset's accountId
// Empty strings from form inputs are converted to null for optional fields
export const serviceCaseSchema = z.object({
  assetId: z.string().uuid('Please select a valid asset'),
  accountId: optionalUuidField,
  contactId: optionalUuidField,
  subject: z.string().min(1, 'Subject is required').max(255),
  description: z.string().min(1, 'Description is required'),
  status: z.enum(serviceCaseStatuses).default('open'),
  priority: z.enum(serviceCasePriorities).default('medium'),
  category: z.enum(serviceCaseCategories).default('other'),
  channel: z.preprocess(
    (val) => (val === '' || val === undefined ? null : val),
    z.enum(serviceCaseChannels).nullable()
  ),
  assignedToId: optionalUuidField,
  dueDate: optionalDatetimeField,
  relatedPartNumber: z.preprocess(
    (val) => (val === '' || val === undefined ? null : val),
    z.string().max(100).nullable()
  ),
  relatedBomPosition: z.number().int().optional().nullable(),
})

export type ServiceCaseInput = z.infer<typeof serviceCaseSchema>

// Service Case Comment validation schema
export const serviceCaseCommentSchema = z.object({
  body: z.string().min(1, 'Comment is required'),
  isInternal: z.boolean().default(false),
})

export type ServiceCaseCommentInput = z.infer<typeof serviceCaseCommentSchema>

// Service Case full entity type
export interface ServiceCaseRecord extends BaseEntity {
  caseNumber: string
  assetId: string
  accountId: string
  contactId: string | null
  subject: string
  description: string
  status: ServiceCaseStatus
  priority: ServiceCasePriority
  category: ServiceCaseCategory
  channel: ServiceCaseChannel | null
  assignedToId: string | null
  createdById: string
  dueDate: Date | null
  firstResponseAt: Date | null
  resolvedAt: Date | null
  closedAt: Date | null
  relatedPartNumber: string | null
  relatedBomPosition: number | null
}

export interface ServiceCaseWithRelations extends ServiceCaseRecord {
  asset?: { id: string; serialNumber: string }
  account?: { id: string; name: string }
  contact?: { id: string; firstName: string | null; lastName: string } | null
  assignedTo?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
  createdBy?: { id: string; firstName: string | null; lastName: string | null; email: string }
}

export interface ServiceCaseCommentRecord {
  id: string
  caseId: string
  authorId: string
  body: string
  isInternal: boolean
  createdAt: Date
  updatedAt: Date
}

export interface ServiceCaseCommentWithAuthor extends ServiceCaseCommentRecord {
  author: { id: string; firstName: string | null; lastName: string | null; email: string }
}

export interface ServiceCaseWithComments extends ServiceCaseWithRelations {
  comments: ServiceCaseCommentWithAuthor[]
}

// ===========================================
// SERVICE HUB: ASSET DOCUMENTS SCHEMAS
// ===========================================

export const assetDocumentTypes = ['manual', 'wiring_diagram', 'ce_declaration', 'parts_list', 'other'] as const
export type AssetDocumentType = typeof assetDocumentTypes[number]

export const assetDocumentSchema = z.object({
  assetId: uuidField.optional().nullable(),
  productId: uuidField.optional().nullable(),
  title: z.string().min(1, 'Title is required').max(255),
  documentType: z.enum(assetDocumentTypes),
  storageKey: z.string().min(1, 'Storage key is required').max(500),
  fileSizeBytes: z.number().int().optional().nullable(),
  mimeType: z.string().max(100).optional().nullable(),
  language: z.string().max(10).default('en'),
  version: z.string().max(20).optional().nullable(),
})

export type AssetDocumentInput = z.infer<typeof assetDocumentSchema>

export interface AssetDocumentRecord {
  id: string
  assetId: string | null
  productId: string | null
  title: string
  documentType: AssetDocumentType
  storageKey: string
  fileSizeBytes: number | null
  mimeType: string | null
  language: string
  version: string | null
  contentText: string | null
  createdAt: Date
  updatedAt: Date
}

// ===========================================
// SERVICE HUB: ASSET BOM ITEMS SCHEMAS
// ===========================================

export const assetBomItemSchema = z.object({
  assetId: uuidField,
  position: z.number().int().min(1, 'Position is required'),
  partNumber: z.string().min(1, 'Part number is required').max(100),
  description: z.string().optional().nullable(),
  quantity: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Invalid quantity').default('1'),
  hotspotX: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Invalid coordinate').optional().nullable(),
  hotspotY: z.string().regex(/^\d+(\.\d{1,4})?$/, 'Invalid coordinate').optional().nullable(),
  diagramRef: z.string().max(255).optional().nullable(),
  supersededBy: z.string().max(100).optional().nullable(),
  listPrice: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Invalid price').optional().nullable(),
  currency: z.string().max(3).default('EUR'),
  twinPartId: z.string().max(255).optional().nullable(),
})

export type AssetBomItemInput = z.infer<typeof assetBomItemSchema>

// Schema for PATCH endpoint - update BOM item's twinPartId link
export const assetBomItemPatchSchema = z.object({
  twinPartId: z.string().max(255).nullable(),
})

export type AssetBomItemPatchInput = z.infer<typeof assetBomItemPatchSchema>

export interface AssetBomItemRecord {
  id: string
  assetId: string
  position: number
  partNumber: string
  description: string | null
  quantity: string
  hotspotX: string | null
  hotspotY: string | null
  diagramRef: string | null
  supersededBy: string | null
  listPrice: string | null
  currency: string
  twinPartId: string | null
  createdAt: Date
  updatedAt: Date
}

// ===========================================
// TWIN ENGINE: SAMPLE DOCUMENT TYPES
// ===========================================

export interface TwinSampleDocument {
  title: string
  description: string | null
  language: string
  pages: number
  url: string
  file_size_bytes: number
}

// ===========================================
// CPQ MODULE: PRODUCTS SCHEMAS
// ===========================================

// Product item types (SAP-aligned)
export const productItemTypes = ['itItems', 'itService'] as const
export type ProductItemType = typeof productItemTypes[number]

// Product validation statuses (SAP-aligned)
export const productValidStatuses = ['tYES', 'tNO'] as const
export type ProductValidStatus = typeof productValidStatuses[number]

// Product sync statuses
export const productSyncStatuses = ['local', 'synced', 'conflict', 'pending_sync'] as const
export type ProductSyncStatus = typeof productSyncStatuses[number]

// Product sources
export const productSources = ['manual', 'sap_b1', 'sap_s4', 'dynamics', 'csv_import'] as const
export type ProductSource = typeof productSources[number]

// Product validation schema
export const productSchema = z.object({
  itemCode: z.string().min(1, 'Item code is required').max(50),
  itemName: z.string().min(1, 'Item name is required').max(100),
  foreignName: z.string().max(100).optional().nullable(),
  itemType: z.enum(productItemTypes).default('itItems'),

  // Flags
  salesItem: z.boolean().default(true),
  purchaseItem: z.boolean().default(false),
  inventoryItem: z.boolean().default(true),

  // Categorization
  itemsGroupCode: z.number().int().optional().nullable(),
  itemsGroupName: z.string().optional().nullable(),

  // Identification
  barCode: z.string().max(254).optional().nullable(),
  manufacturerCode: z.number().int().optional().nullable(),
  supplierCatalogNo: z.string().optional().nullable(),

  // Units of Measure
  salesUnit: z.string().max(20).default('Stück'),
  salesItemsPerUnit: z.string().regex(/^\d+(\.\d{1,6})?$/).default('1').optional(),
  salesPackagingUnit: z.string().max(20).optional().nullable(),
  salesQtyPerPackUnit: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  purchaseUnit: z.string().max(20).optional().nullable(),
  purchaseItemsPerUnit: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),

  // Weight & Dimensions
  salesUnitWeight: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  salesWeightUnit: z.string().max(10).optional().nullable(),
  salesUnitVolume: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  salesVolumeUnit: z.string().max(10).optional().nullable(),

  // Default Warehouse
  defaultWarehouse: z.string().max(8).optional().nullable(),

  // Status
  valid: z.enum(productValidStatuses).default('tYES'),
  frozen: z.enum(productValidStatuses).default('tNO'),

  // JustSell Extensions
  description: z.string().optional().nullable(),
  shortDescription: z.string().optional().nullable(),
  imageUrl: z.string().url().optional().nullable(),

  // Pricing
  defaultPrice: z.string().regex(/^\d+(\.\d{1,2})?$/).optional().nullable(),
  defaultCurrency: z.string().max(3).default('EUR'),
  vatGroup: z.string().optional().nullable(),
})

export type ProductInput = z.infer<typeof productSchema>

// Product Group validation schema
export const productGroupSchema = z.object({
  groupCode: z.number().int().min(1, 'Group code is required'),
  groupName: z.string().min(1, 'Group name is required'),
  parentGroupCode: z.number().int().optional().nullable(),
})

export type ProductGroupInput = z.infer<typeof productGroupSchema>

// Product Price validation schema
export const productPriceSchema = z.object({
  productId: uuidField,
  priceListCode: z.number().int().min(1, 'Price list code is required'),
  priceListName: z.string().optional().nullable(),
  price: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Invalid price'),
  currency: z.string().max(3).default('EUR'),
  basePriceListCode: z.number().int().optional().nullable(),
  factor: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  validFrom: datetimeField.optional().nullable(),
  validTo: datetimeField.optional().nullable(),
})

export type ProductPriceInput = z.infer<typeof productPriceSchema>

// ===========================================
// CPQ MODULE: QUOTES SCHEMAS
// ===========================================

// Quote statuses
export const quoteStatuses = ['draft', 'pending_approval', 'approved', 'sent', 'accepted', 'rejected', 'expired'] as const
export type QuoteStatus = typeof quoteStatuses[number]

// Quote validation schema
export const quoteSchema = z.object({
  accountId: uuidField.optional().nullable(),
  contactId: uuidField.optional().nullable(),
  cardCode: z.string().max(15).optional().nullable(),
  cardName: z.string().max(100).optional().nullable(),
  opportunityId: uuidField.optional().nullable(),
  docDate: datetimeField,
  validUntil: datetimeField,
  docCurrency: z.string().max(3).default('EUR'),
  discountPercent: z.string().regex(/^\d+(\.\d{1,2})?$/).default('0').optional(),
  status: z.enum(quoteStatuses).default('draft'),
  ownerId: uuidField.optional().nullable(),
})

export type QuoteInput = z.infer<typeof quoteSchema>

// Quote Line Item validation schema
export const quoteLineItemSchema = z.object({
  quoteId: uuidField,
  lineNum: z.number().int().min(0),
  productId: uuidField.optional().nullable(),
  proxyProductId: uuidField.optional().nullable(),
  itemCode: z.string().max(50).optional().nullable(),
  itemDescription: z.string().min(1, 'Description is required'),
  quantity: z.string().regex(/^\d+(\.\d{1,6})?$/, 'Invalid quantity'),
  uomCode: z.string().max(20).optional().nullable(),
  listPrice: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  offerPrice: z.string().regex(/^\d+(\.\d{1,6})?$/, 'Offer price is required'),
  discountPercent: z.string().regex(/^\d+(\.\d{1,2})?$/).default('0').optional(),
  lineTotal: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Line total is required'),
  costPrice: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  marginPercent: z.string().regex(/^-?\d+(\.\d{1,2})?$/).optional().nullable(),
})

export type QuoteLineItemInput = z.infer<typeof quoteLineItemSchema>

// ===========================================
// CPQ MODULE: ORDERS SCHEMAS
// ===========================================

// Order statuses
export const orderStatuses = ['draft', 'confirmed', 'processing', 'shipped', 'delivered', 'invoiced', 'cancelled'] as const
export type OrderStatus = typeof orderStatuses[number]

// Order sync statuses
export const orderSyncStatuses = ['local', 'synced', 'conflict', 'push_failed', 'push_pending'] as const
export type OrderSyncStatus = typeof orderSyncStatuses[number]

// Order sources
export const orderSources = ['manual', 'pdf_import', 'sap_sync', 'api'] as const
export type OrderSource = typeof orderSources[number]

// Order validation schema
export const orderSchema = z.object({
  docType: z.string().default('dDocument_Items').optional(),
  docDate: datetimeField,
  docDueDate: datetimeField.optional().nullable(),
  taxDate: datetimeField.optional().nullable(),
  accountId: uuidField.optional().nullable(),
  contactId: uuidField.optional().nullable(),
  cardCode: z.string().max(15).optional().nullable(),
  cardName: z.string().max(100).optional().nullable(),
  customerRefNumber: z.string().max(50).optional().nullable(),
  opportunityId: uuidField.optional().nullable(),
  quoteId: uuidField.optional().nullable(),
  shipToCode: z.string().max(50).optional().nullable(),
  payToCode: z.string().max(50).optional().nullable(),
  shippingAddress: z.string().optional().nullable(),
  billingAddress: z.string().optional().nullable(),
  docCurrency: z.string().max(3).default('EUR'),
  discountPercent: z.string().regex(/^\d+(\.\d{1,2})?$/).default('0').optional(),
  paymentTermsCode: z.number().int().optional().nullable(),
  paymentTermsName: z.string().optional().nullable(),
  shippingTypeCode: z.number().int().optional().nullable(),
  shippingTypeName: z.string().optional().nullable(),
  status: z.enum(orderStatuses).default('draft'),
  comments: z.string().optional().nullable(),
  ownerId: uuidField.optional().nullable(),
})

export type OrderInput = z.infer<typeof orderSchema>

// Order Line Item validation schema
export const orderLineItemSchema = z.object({
  orderId: uuidField,
  lineNum: z.number().int().min(0),
  productId: uuidField.optional().nullable(),
  itemCode: z.string().max(50).optional().nullable(),
  itemDescription: z.string().min(1, 'Description is required'),
  quantity: z.string().regex(/^\d+(\.\d{1,6})?$/, 'Invalid quantity'),
  uomCode: z.string().max(20).optional().nullable(),
  uomEntry: z.number().int().optional().nullable(),
  unitPrice: z.string().regex(/^\d+(\.\d{1,6})?$/, 'Unit price is required'),
  price: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  currency: z.string().max(3).default('EUR'),
  discountPercent: z.string().regex(/^\d+(\.\d{1,2})?$/).default('0').optional(),
  lineTotal: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Line total is required'),
  vatGroup: z.string().max(8).optional().nullable(),
  vatPercent: z.string().regex(/^\d+(\.\d{1,2})?$/).optional().nullable(),
  warehouseCode: z.string().max(8).optional().nullable(),
  shipDate: datetimeField.optional().nullable(),
})

export type OrderLineItemInput = z.infer<typeof orderLineItemSchema>

// ===========================================
// CPQ MODULE: PROXY PRODUCTS SCHEMAS
// ===========================================

// Proxy Product statuses
export const proxyProductStatuses = ['unresolved', 'matched', 'created_in_erp', 'rejected'] as const
export type ProxyProductStatus = typeof proxyProductStatuses[number]

// Proxy Product raw data type
export interface ProxyProductRawData {
  source: 'pdf' | 'manual' | 'email'
  originalText?: string
  extractedFields: Record<string, unknown>
  confidence: number // 0-100
}

// Proxy Product validation schema
export const proxyProductSchema = z.object({
  skuGuess: z.string().optional().nullable(),
  descriptionExtracted: z.string().optional().nullable(),
  manufacturerGuess: z.string().optional().nullable(),
  rawData: z.object({
    source: z.enum(['pdf', 'manual', 'email']),
    originalText: z.string().optional(),
    extractedFields: z.record(z.unknown()),
    confidence: z.number().min(0).max(100),
  }),
  extractedPrice: z.string().regex(/^\d+(\.\d{1,6})?$/).optional().nullable(),
  extractedCurrency: z.string().max(3).default('EUR'),
  extractedUnit: z.string().optional().nullable(),
  status: z.enum(proxyProductStatuses).default('unresolved'),
  sourceQuoteId: uuidField.optional().nullable(),
  sourceOrderId: uuidField.optional().nullable(),
})

export type ProxyProductInput = z.infer<typeof proxyProductSchema>

// Proxy Product resolve schema
export const proxyProductResolveSchema = z.object({
  resolvedToProductId: uuidField,
  resolutionNotes: z.string().optional().nullable(),
})

export type ProxyProductResolveInput = z.infer<typeof proxyProductResolveSchema>

// ===========================================
// CPQ MODULE: FULL ENTITY TYPES
// ===========================================

export interface ProductRecord extends BaseEntity {
  itemCode: string
  itemName: string
  foreignName: string | null
  itemType: ProductItemType
  salesItem: boolean
  purchaseItem: boolean
  inventoryItem: boolean
  itemsGroupCode: number | null
  itemsGroupName: string | null
  barCode: string | null
  manufacturerCode: number | null
  supplierCatalogNo: string | null
  salesUnit: string
  salesItemsPerUnit: string | null
  salesPackagingUnit: string | null
  salesQtyPerPackUnit: string | null
  purchaseUnit: string | null
  purchaseItemsPerUnit: string | null
  salesUnitWeight: string | null
  salesWeightUnit: string | null
  salesUnitVolume: string | null
  salesVolumeUnit: string | null
  defaultWarehouse: string | null
  valid: ProductValidStatus
  frozen: ProductValidStatus
  description: string | null
  shortDescription: string | null
  imageUrl: string | null
  defaultPrice: string | null
  defaultCurrency: string
  vatGroup: string | null
  source: ProductSource
  externalId: string | null
  externalData: unknown | null
  externalCreatedAt: Date | null
  externalUpdatedAt: Date | null
  lastSyncedAt: Date | null
  syncStatus: ProductSyncStatus
}

export interface ProductGroupRecord {
  id: string
  groupCode: number
  groupName: string
  parentGroupCode: number | null
  source: string
  externalId: string | null
  lastSyncedAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export interface ProductPriceRecord {
  id: string
  productId: string
  priceListCode: number
  priceListName: string | null
  price: string
  currency: string
  basePriceListCode: number | null
  factor: string | null
  validFrom: Date | null
  validTo: Date | null
  lastSyncedAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export interface QuoteRecord extends BaseEntity {
  quoteNumber: string
  accountId: string | null
  contactId: string | null
  cardCode: string | null
  cardName: string | null
  opportunityId: string | null
  docDate: Date
  validUntil: Date
  docCurrency: string
  docTotal: string | null
  vatSum: string | null
  discountPercent: string
  status: QuoteStatus
  requiresApproval: boolean
  approvalReason: string | null
  approvedById: string | null
  approvedAt: Date | null
  convertedToOrderId: string | null
  convertedAt: Date | null
  externalId: string | null
  externalDocNum: string | null
}

export interface QuoteLineItemRecord {
  id: string
  quoteId: string
  lineNum: number
  productId: string | null
  proxyProductId: string | null
  itemCode: string | null
  itemDescription: string
  quantity: string
  uomCode: string | null
  listPrice: string | null
  offerPrice: string
  discountPercent: string
  lineTotal: string
  costPrice: string | null
  marginPercent: string | null
  createdAt: Date
  updatedAt: Date
}

export interface OrderRecord extends BaseEntity {
  docType: string
  docDate: Date
  docDueDate: Date | null
  taxDate: Date | null
  orderNumber: string
  accountId: string | null
  contactId: string | null
  cardCode: string | null
  cardName: string | null
  customerRefNumber: string | null
  opportunityId: string | null
  quoteId: string | null
  baseDocEntry: number | null
  baseDocNum: number | null
  shipToCode: string | null
  payToCode: string | null
  shippingAddress: string | null
  billingAddress: string | null
  shippingAddressJson: { street: string; city: string; postalCode: string; country: string; state?: string } | null
  billingAddressJson: { street: string; city: string; postalCode: string; country: string; state?: string } | null
  docCurrency: string
  docRate: string
  docTotal: string | null
  docTotalBeforeDiscount: string | null
  vatSum: string | null
  discountPercent: string
  paymentTermsCode: number | null
  paymentTermsName: string | null
  shippingTypeCode: number | null
  shippingTypeName: string | null
  documentStatus: string
  cancelled: string
  status: OrderStatus
  comments: string | null
  source: OrderSource
  externalId: string | null
  externalDocNum: string | null
  externalData: unknown | null
  syncDirection: string | null
  lastSyncedAt: Date | null
  syncStatus: OrderSyncStatus
  syncError: string | null
  sourcePdfStorageKey: string | null
  sourcePdfFilename: string | null
}

export interface OrderLineItemRecord {
  id: string
  orderId: string
  lineNum: number
  productId: string | null
  itemCode: string | null
  itemDescription: string
  quantity: string
  uomCode: string | null
  uomEntry: number | null
  unitPrice: string
  price: string | null
  currency: string
  discountPercent: string
  lineTotal: string
  vatGroup: string | null
  vatPercent: string | null
  warehouseCode: string | null
  shipDate: Date | null
  externalLineNum: number | null
  createdAt: Date
  updatedAt: Date
}

export interface ProxyProductRecord {
  id: string
  skuGuess: string | null
  descriptionExtracted: string | null
  manufacturerGuess: string | null
  rawData: ProxyProductRawData
  extractedPrice: string | null
  extractedCurrency: string
  extractedUnit: string | null
  status: ProxyProductStatus
  resolvedToProductId: string | null
  resolvedById: string | null
  resolvedAt: Date | null
  resolutionNotes: string | null
  sourceQuoteId: string | null
  sourceOrderId: string | null
  createdAt: Date
  updatedAt: Date
}

// Extended types with relations
export interface ProductWithPrices extends ProductRecord {
  prices?: ProductPriceRecord[]
}

export interface QuoteWithRelations extends QuoteRecord {
  account?: { id: string; name: string } | null
  contact?: { id: string; firstName: string | null; lastName: string } | null
  opportunity?: { id: string; name: string } | null
  owner?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
  approvedBy?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
  lineItems?: QuoteLineItemRecord[]
  _count?: { lineItems: number }
}

export interface OrderWithRelations extends OrderRecord {
  account?: { id: string; name: string } | null
  contact?: { id: string; firstName: string | null; lastName: string } | null
  opportunity?: { id: string; name: string } | null
  quote?: { id: string; quoteNumber: string } | null
  owner?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
  lineItems?: OrderLineItemRecord[]
  _count?: { lineItems: number }
}

export interface ProxyProductWithRelations extends ProxyProductRecord {
  resolvedToProduct?: { id: string; itemCode: string; itemName: string } | null
  resolvedBy?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
}
